import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { apiResponse } from 'src/app/models/apiResponse.model';
import { ApiFilter, FieldsSelection, DefaultFilter, GroupByConfig } from 'src/app/models/apiParams.model';
import { BaseApiService } from '../base/base-api.service';

/**
 * Vehicle Service - Handles vehicle types, makes, models, body types, colors, fuel types,
 * transmission types, drive types, condition types, VAT margin types, origin channels,
 * and related vehicle entities
 */
@Injectable({ providedIn: 'root' })
export class VehicleService extends BaseApiService {
    private readonly vehiclePath = '/request/v1/';

    constructor(http: HttpClient) {
        super(http);
    }

    /*-------------------------
    == VEHICLE TYPE CALLS ==
    -------------------------*/

    getVehicleTypes(): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}vehicle-type/`);
    }

    getVehicleTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}vehicle-type/`, id);
    }

    /*-------------------------
    == MAKE CALLS ==
    -------------------------*/

    getMakes(): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}make/`);
    }

    getMakeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}make/`, id);
    }

    /*-------------------------
    == MODEL CALLS ==
    -------------------------*/

    getModels(limit: number, page: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}model`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getModelID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}model`, id);
    }

    getModelSpecifics(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}model-specific/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getModelSpecificsID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}model-specific/`, id);
    }

    /*-------------------------
    == BODY TYPE CALLS ==
    -------------------------*/

    getBodyTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}body-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getBodyTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}body-type/`, id);
    }

    /*-------------------------
    == COLOR TYPE CALLS ==
    -------------------------*/

    getColorTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}color-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getColorTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}color-type/`, id);
    }

    getInteriorColorTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}interior-color-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getInteriorColorTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}interior-color-type/`, id);
    }

    /*-------------------------
    == CONDITION TYPE CALLS ==
    -------------------------*/

    getConditionTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}condition-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getConditionTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}condition-type/`, id);
    }

    /*-------------------------
    == FUEL TYPE CALLS ==
    -------------------------*/

    getFuelTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}fuel-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getFuelTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}fuel-type/`, id);
    }

    /*-------------------------
    == TRANSMISSION TYPE CALLS ==
    -------------------------*/

    getTransmissionTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}transmission-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getTransmissionTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}transmission-type/`, id);
    }

    /*-------------------------
    == DRIVE TYPE CALLS ==
    -------------------------*/

    getDriveTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}drive-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getDriveTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}drive-type/`, id);
    }

    /*-------------------------
    == VAT MARGIN TYPE CALLS ==
    -------------------------*/

    getVatMarginTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}vat-margin-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getVatMarginTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}vat-margin-type/`, id);
    }

    /*-------------------------
    == ORIGIN CHANNEL TYPE CALLS ==
    -------------------------*/

    getOriginChannelTypes(): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}origin-channel-type/`);
    }

    getOriginCommercialTypes(): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}origin-commercial-type/`);
    }

    /*-------------------------
    == COUNTRY OF ORIGIN TYPE CALLS ==
    -------------------------*/

    getCountryOfOriginTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}country-of-origin-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getCountryOfOriginTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}country-of-origin-type/`, id);
    }

    /*-------------------------
    == USED AS TYPE CALLS ==
    -------------------------*/

    getUsedAsTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}used-as-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getUsedAsTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}used-as-type/`, id);
    }

    /*-------------------------
    == AIRCO STATUS TYPE CALLS ==
    -------------------------*/

    getAircoStatusTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}airco-status-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getAircoStatusTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}airco-status-type/`, id);
    }

    /*-------------------------
    == INTERIOR TOUCH TYPE CALLS ==
    -------------------------*/

    getInteriorTouchTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}interior-touch-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getInteriorTouchTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}interior-touch-type/`, id);
    }

    /*-------------------------
    == LAC TYPE CALLS ==
    -------------------------*/

    getLacTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}lac-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getLacTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}lac-type/`, id);
    }

    /*-------------------------
    == LINING TYPE CALLS ==
    -------------------------*/

    getLiningTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}lining-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getLiningTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}lining-type/`, id);
    }

    /*-------------------------
    == MAINTENANCE GUIDE TYPE CALLS ==
    -------------------------*/

    getMaintenanceGuideTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}maintenance-guide-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getMaintenanceGuideID(_id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}maintenance-guide-type/`, _id);
    }

    /*-------------------------
    == MAINTENANCE HISTORY TYPE CALLS ==
    -------------------------*/

    getMaintenanceHistoryTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}maintenance-history-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getMaintenanceHistoryTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}maintenance-history-type/`, id);
    }

    /*-------------------------
    == MILEAGE HISTORY STATUS TYPE CALLS ==
    -------------------------*/

    getMileageHistoryStatusTypes(limit?: number, page?: number, orderBy?: string | null, filter?: string | ApiFilter | undefined, fields?: FieldsSelection | undefined, defaultFilter?: DefaultFilter | undefined, groupBy?: GroupByConfig | undefined): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.vehiclePath}mileage-history-status-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    getMileageHistoryStatusTypeID(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.vehiclePath}mileage-history-status-type/`, id);
    }
}
